/* eslint-disable no-param-reassign */
import { useEffect, useMemo } from "react";
import * as React from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";

import { Alert, Button, Tab, TabsList, TabsWrapper } from "@livechat/design-system-react-components";

import { ExternalLink } from "components/external-link";
import Block from "components/layout/Block";
import Text from "components/layout/Text";
import { StyledLink } from "components/links/StyledLink";
import { EventPlace, trackEngagement } from "helpers/analytics";
import SettingsViewContainer from "routes/settings/ViewContainer";
import { SettingsRoutes } from "routes/settings/constants";
import { ContentWrapper, PageWrapper } from "routes/settings/styled";
import {
  canManageApplications,
  getInReviewApplicationsCount,
  getInstalledApplicationsCount,
  getPrivateApplicationsCount,
} from "store/entities/applications";

import { IntegrationsTab } from "../../constants";

import TabsContent from "./TabContent";

interface IIntegrationTabItem {
  id: string;
  title: string;
  slug: string;
  count: number | null;
}

const MainPage: React.FC = () => {
  const history = useHistory();
  const { slug } = useParams<{ slug: string }>();

  const installedAppsCount = useSelector(getInstalledApplicationsCount);
  const privateAppsCount = useSelector(getPrivateApplicationsCount);
  const inReviewAppsCount = useSelector(getInReviewApplicationsCount);
  const canManageApps = useSelector(canManageApplications);
  const shouldDisplayInReviewApps = canManageApps && !!inReviewAppsCount;

  useEffect(() => {
    trackEngagement("Settings: Integrations section opened", EventPlace.Settings);
  }, []);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleLearnMoreClick = () => {
    trackEngagement("Integrations: Learn more clicked", EventPlace.Settings);
  };

  const tabItems: IIntegrationTabItem[] = useMemo(
    () => [
      { id: IntegrationsTab.installed, title: "Installed apps", count: installedAppsCount, slug: "/installed" },
      ...(canManageApps
        ? [
            {
              id: IntegrationsTab.private,
              title: "Private apps",
              count: privateAppsCount || null,
              slug: "/private",
            },
          ]
        : []),
      ...(shouldDisplayInReviewApps
        ? [
            {
              id: IntegrationsTab.inReview,
              title: "In review",
              count: inReviewAppsCount || null,
              slug: "/in-review",
            },
          ]
        : []),
    ],
    [installedAppsCount, privateAppsCount, inReviewAppsCount, canManageApps, shouldDisplayInReviewApps]
  );

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const openTab = (slug: string) => {
    const tabUrl = `${SettingsRoutes.Integrations}${slug}`;
    history.push(tabUrl);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const openMarketplace = () => {
    trackEngagement("Integrations: Explore Marketplace button clicked", EventPlace.Settings);
    history.push("/marketplace");
  };

  return (
    <SettingsViewContainer title="Apps and integrations">
      <PageWrapper>
        <ContentWrapper>
          <Block marginBottom="36px">
            <Text marginBottom="12px" fontSize="14px" lineHeight="20px">
              Install your preferred apps or integrate the HelpDesk account with other solutions to power it up and
              handle customer cases even more efficiently.{" "}
              <ExternalLink href="https://www.helpdesk.com/integrations/" onClick={handleLearnMoreClick}>
                Learn more
              </ExternalLink>
            </Text>

            <Button kind="primary" onClick={openMarketplace}>
              Explore HelpDesk Marketplace
            </Button>
          </Block>

          {!canManageApps && (
            <Block maxWidth="800px" marginBottom="36px">
              <Alert kind="info">
                <Text fontSize="15px" lineHeight="22px">
                  You can&apos;t manage apps and integrations in your current HelpDesk role.{" "}
                  <StyledLink kind="link-light" to="/agents">
                    Contact any
                  </StyledLink>{" "}
                  admin to change your role to admin.
                </Text>
              </Alert>
            </Block>
          )}

          <TabsWrapper>
            <TabsList>
              {tabItems.map(({ id, title, slug: tabSlug }) => (
                <Tab key={id} onClick={() => openTab(tabSlug)} isSelected={id === slug}>
                  <Text fontSize="15px" paddingTop="10px" paddingBottom="10px">
                    {title}
                  </Text>
                </Tab>
              ))}
            </TabsList>
          </TabsWrapper>

          <TabsContent selected={slug} />
        </ContentWrapper>
      </PageWrapper>
    </SettingsViewContainer>
  );
};

export default MainPage;
